import React from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Stack, Typography } from '@mui/material';
import {
  BarList, Chart, EmptyState, ListTable, MetricCard, Panel, StatusChip, type ListColumn,
} from '../../../ui-kit';
import { getChartPalette } from '../../../theme/charts';
import {
  fetchCcSpotlight, type CcCase, type CcSpotlight,
} from '../../../services/carrierCorrespondenceService';
import { useLive } from '../rocco/live';
import { SIDE, ageTone, money, notAnswered, stalledFor } from './status';
import CaseStory from './CaseStory';

const EMPTY: CcSpotlight = {
  totals: {
    stalled: 0, median_days_stalled: null, longest_days_stalled: null,
    investigated: 0, calls_made: 0, waiting_on_us: 0, hold_seconds: 0,
  },
  carriers: [], by_stage: [], by_carrier: [], whose_move: [], oldest: [],
};

/**
 * The payload, with anything missing filled in from EMPTY.
 *
 * Field by field, not `data ?? EMPTY`: that only helps when the whole payload
 * is absent, and the case that actually bites is a payload one field behind —
 * the API and the screens deploy separately, so for a minute after a release
 * one of them is older than the other. A missing array meeting `.map` takes
 * down the whole page, which is a poor trade for a figure that could simply
 * have read zero until the next poll.
 */
export const withDefaults = (data: CcSpotlight | null): CcSpotlight => ({
  ...EMPTY,
  ...(data ?? {}),
  totals: { ...EMPTY.totals, ...(data?.totals ?? {}) },
  carriers: data?.carriers ?? EMPTY.carriers,
  by_stage: (data?.by_stage ?? EMPTY.by_stage).map((row) => ({ ...row, by_carrier: row.by_carrier ?? {} })),
  by_carrier: data?.by_carrier ?? EMPTY.by_carrier,
  whose_move: data?.whose_move ?? EMPTY.whose_move,
  oldest: data?.oldest ?? EMPTY.oldest,
});

const days = (value: number | null) => (value == null ? '—' : stalledFor(value));

/** "13 hours", "45 minutes": whole units a person reads at a glance. */
const holdTime = (seconds: number | null | undefined): string => {
  const minutes = Math.round((seconds ?? 0) / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'}`;
};

/** "13 Aug": a duration always shows the date it counts from. */
const dayOf = (iso: string | null | undefined) => (iso
  ? new Date(iso).toLocaleDateString([], { day: 'numeric', month: 'short' })
  : '');

/**
 * The page this agent opens on, and the one a demo is given from.
 *
 * What the AI found leads, because that is what is new: a pending book always
 * has stalled cases, and an agency principal already knows that. What they have
 * never had is the reason — and above all how many are waiting on *us*, since
 * each of those is something an advisor can clear this afternoon.
 */
export default function SpotlightPage() {
  const theme = useTheme();
  const { data, error, loading } = useLive(fetchCcSpotlight, 20_000);
  const [open, setOpen] = React.useState<string | null>(null);
  if (error && !data) return <EmptyState message={error} />;

  const found = withDefaults(data ?? null);
  const { totals } = found;
  const palette = getChartPalette(undefined);

  // One full-width chart answering both "where do cases get stuck" and "with
  // whom", by stacking each stage's bar by carrier. Reversed because a
  // category axis draws its first entry at the bottom, and the busiest stage
  // belongs at the top.
  const stages = [...found.by_stage].reverse();
  const stageOption = {
    grid: { left: 8, right: 56, top: 8, bottom: 8, containLabel: true },
    legend: { bottom: 0, textStyle: { color: theme.tokens.text.secondary } },
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    // No scale drawn: every bar carries its own total at the end of it.
    xAxis: { type: 'value', show: false },
    yAxis: {
      type: 'category',
      data: stages.map((s) => s.stage),
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: { color: theme.tokens.text.secondary },
    },
    series: found.carriers.map((carrier, index) => ({
      name: carrier,
      type: 'bar',
      stack: 'cases',
      barMaxWidth: 26,
      data: stages.map((s) => s.by_carrier[carrier] ?? 0),
      itemStyle: {
        color: palette[index % palette.length],
        borderColor: theme.tokens.surface.raised,
        borderWidth: 1,
      },
      ...(index === found.carriers.length - 1 ? {
        label: {
          show: true,
          position: 'right',
          color: theme.tokens.text.secondary,
          formatter: (p: { dataIndex: number }) => String(stages[p.dataIndex]?.cases ?? ''),
        },
      } : {}),
    })),
  };

  const oldestColumns: ListColumn<CcCase>[] = [
    {
      key: 'stalled',
      header: 'Stalled for',
      width: 104,
      render: (row) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({
            fontWeight: 600,
            color: ageTone(row.days_stalled) === 'bad' ? t.tokens.semantic.danger : t.tokens.semantic.warning,
          })}>
            {stalledFor(row.days_stalled)}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            since {dayOf(row.stage_since)}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'case',
      header: 'Case',
      grow: 1.2,
      minWidth: 150,
      render: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, overflowWrap: 'anywhere' })}>
            {row.applicant}
          </Typography>
          <Typography variant="caption" sx={(t) => ({
            color: t.tokens.text.tertiary, fontVariantNumeric: 'tabular-nums',
          })}>
            {row.policy_num}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'carrier',
      header: 'Carrier',
      width: 140,
      render: (row) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, overflowWrap: 'anywhere' })}>
          {row.carrier}
        </Typography>
      ),
    },
    {
      key: 'face',
      header: 'Face amount',
      width: 130,
      align: 'right',
      render: (row) => (
        <Typography variant="body2" sx={(t) => ({
          color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums',
        })}>
          {money(row.face_amount)}
        </Typography>
      ),
    },
    {
      key: 'stage',
      header: 'Stuck at',
      grow: 1,
      minWidth: 150,
      render: (row) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, overflowWrap: 'anywhere' })}>
          {row.stage}
        </Typography>
      ),
    },
    {
      key: 'side',
      header: 'Waiting on',
      width: 158,
      render: (row) => (row.blocking_side
        ? <StatusChip {...SIDE[row.blocking_side]} />
        : <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{notAnswered(row, { short: true })}</Typography>),
    },
  ];

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(5, 1fr)' } }}>
        <MetricCard
          loading={loading}
          label="Cases stalled"
          value={String(totals.stalled)}
          info="Applications sitting with a carrier that have not moved in more than five days."
        />
        <MetricCard
          loading={loading}
          label="Cases investigated by AI"
          value={String(totals.investigated)}
          info="Cases where the AI rang the carrier and wrote back what is outstanding, instead of somebody holding on the phone."
        />
        <MetricCard
          loading={loading}
          label="Calls made by AI"
          value={totals.calls_made.toLocaleString()}
          info="Every call to a carrier, including the ones nobody picked up. The AI rings again every two days, up to three times."
        />
        <MetricCard
          loading={loading}
          label="Call Hours Handled by AI"
          value={holdTime(totals.hold_seconds)}
          info="Time the AI spent on hold and on the phone with carriers. Without it, this would have been a person's time."
        />
        <MetricCard
          loading={loading}
          label="Longest waiting"
          value={days(totals.longest_days_stalled)}
          note={found.oldest[0]?.stage_since ? `since ${dayOf(found.oldest[0].stage_since)}` : undefined}
          info={`The case that has sat the longest without moving. The usual wait is ${days(totals.median_days_stalled)}.`}
        />
      </Box>

      <Panel
        title="Where cases get stuck"
        subtitle="Every stalled case by the stage it is sitting in, split by carrier"
        loading={loading}
        loadingHeight={320}
      >
        {found.by_stage.length ? (
          <Chart
            option={stageOption}
            height={320}
            ariaLabel="Stalled cases by the stage they are stuck in, each bar split by carrier"
          />
        ) : <EmptyState message="Nothing is stalled." />}
      </Panel>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        <Panel
          title="Whose move it is"
          subtitle="Of the cases the AI has asked about"
          loading={loading}
          loadingHeight={220}
        >
          {found.whose_move.some((w) => w.cases > 0) ? (
            <BarList
              rows={found.whose_move.map((w) => ({ key: w.side, label: w.label, value: w.cases }))}
              formatValue={(v) => String(v)}
              valueLabel="Cases"
              labelHeading="Waiting on"
            />
          ) : (
            <EmptyState message="No calls made yet. Open a case and ask a carrier what is outstanding." />
          )}
        </Panel>

        <Panel
          title="Which carriers are holding things up"
          subtitle="Stalled cases by carrier"
          loading={loading}
          loadingHeight={220}
        >
          {found.by_carrier.length ? (
            <BarList
              rows={found.by_carrier.map((c) => ({ key: c.carrier, label: c.carrier, value: c.cases }))}
              formatValue={(v) => String(v)}
              valueLabel="Cases"
              labelHeading="Carrier"
            />
          ) : <EmptyState message="Nothing is stalled." />}
        </Panel>
      </Box>

      <Panel
        title="Waiting longest"
        subtitle="The five that have sat the longest without moving"
        loading={loading}
        loadingHeight={280}
      >
        <ListTable
          columns={oldestColumns}
          rows={found.oldest}
          getRowKey={(row) => row.id}
          loading={loading}
          onRowClick={(row) => setOpen(row.id)}
          emptyMessage="Nothing is stalled."
        />
      </Panel>
      {open ? <CaseStory caseId={open} onClose={() => setOpen(null)} /> : null}
    </Stack>
  );
}
